<template>
  <Teleport to="body">
    <div v-if="open" class="ds-lightbox" role="dialog" aria-modal="true" @click.self="$emit('close')">
      <!-- app.scss's .btn-close is already white; `btn-close-white` would invert it. -->
      <button type="button" class="btn-close ds-lightbox__close" aria-label="Close" @click="$emit('close')"></button>

      <img v-if="current" :src="current.fullUrl" :alt="`Photo ${index + 1} of ${photos.length}`" class="ds-lightbox__image" />

      <template v-if="photos.length > 1">
        <button type="button" class="ds-lightbox__nav ds-lightbox__nav--prev" aria-label="Previous photo" @click="$emit('prev')">
          <span class="material-icons">chevron_left</span>
        </button>
        <button type="button" class="ds-lightbox__nav ds-lightbox__nav--next" aria-label="Next photo" @click="$emit('next')">
          <span class="material-icons">chevron_right</span>
        </button>
        <p class="ds-lightbox__counter">{{ index + 1 }} / {{ photos.length }}</p>
      </template>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, watch } from 'vue';
import type { PropType } from 'vue';
import type { HighlightPhoto } from '../../helpers/highlightDetails';

export default defineComponent({
  name: 'DetailSheetLightbox',
  props: {
    open: { type: Boolean, default: false },
    photos: { type: Array as PropType<HighlightPhoto[]>, default: () => [] },
    index: { type: Number, default: 0 },
  },
  emits: ['close', 'prev', 'next'],
  setup(props, { emit }) {
    const current = computed(() => props.photos[props.index] ?? null);

    // Declarative keyboard nav, replacing the hand-rolled innerHTML lightbox the
    // highlight dialogs used to build (and whose listeners leaked on Escape).
    // Bound in the capture phase and stopped so Escape closes the lightbox
    // without also dismissing the Bootstrap modal underneath it.
    const onKeydown = (event: KeyboardEvent) => {
      if (!['Escape', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;

      event.stopPropagation();
      if (event.key === 'Escape') emit('close');
      else if (event.key === 'ArrowLeft') emit('prev');
      else emit('next');
    };

    const stopListening = () => document.removeEventListener('keydown', onKeydown, true);

    // `immediate` so a lightbox that is mounted already open is navigable too,
    // not only one that transitions from closed.
    watch(
      () => props.open,
      (isOpen) => {
        if (isOpen) document.addEventListener('keydown', onKeydown, true);
        else stopListening();
      },
      { immediate: true },
    );

    onBeforeUnmount(stopListening);

    return { current };
  },
});
</script>

<style scoped>
.ds-lightbox {
  position: fixed;
  inset: 0;
  /* Above Bootstrap's modal (1055) so it covers the dialog it opened from. */
  z-index: 2000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

.ds-lightbox__image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.ds-lightbox__close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  opacity: 0.8;
}

.ds-lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ds-lightbox__nav:hover {
  background: rgba(255, 255, 255, 0.28);
}

.ds-lightbox__nav--prev {
  left: 0.75rem;
}

.ds-lightbox__nav--next {
  right: 0.75rem;
}

.ds-lightbox__counter {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  color: var(--text-muted);
  font-size: 0.8125rem;
}
</style>
